import { ActionDialog } from "@/components/ui/action-dialog";
import { fetchTotpStatus, startTotpSetup, completeTotpSetup, changeTotp, verifyTotpChallenge, type TotpSetup, type TotpStatus } from "@/api/auth";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  changePassword,
  createRecentReauthentication,
  deleteAccount,
  departCurrentMembership,
  disableAuthMethod,
  enableLocalAuthMethod,
  fetchAccountDeletionCheck,
  fetchCurrentMembershipDepartureCheck,
  fetchAuthMethods,
  fetchMe,
  startExternalAuthentication,
  startAccountEmailChange,
  type AuthMethodsResponse,
  type ExternalAuthProvider,
} from "@/api/auth";
import { ApiError, clearConsoleBffCsrfToken, notifySessionExpired } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import {
  PASSWORD_DENYLIST_ERROR_MESSAGE,
  PASSWORD_LENGTH_ERROR_MESSAGE,
  PASSWORD_LENGTH_HINT,
  isPasswordLengthValid,
} from "@/features/auth/password-policy";

type Action = "password" | "email" | "delete" | "totp-setup" | "totp-disable" | "totp-codes" | "google-link" | "microsoft-link" | "local-disable" | "google-disable" | "microsoft-disable" | "depart";
const ACTIONS: Action[] = ["password", "email", "delete", "totp-setup", "totp-disable", "totp-codes", "google-link", "microsoft-link", "local-disable", "google-disable", "microsoft-disable", "depart"];
const PROVIDER_LABELS = { local: "パスワード", google: "Google", microsoft: "Microsoft" } as const;

export function AccountSecurityPage() {
  const navigate = useNavigate();
  const [action, setAction] = useState<Action | null>(() => {
    const hint = sessionStorage.getItem("kdrive.auth.accountAction");
    sessionStorage.removeItem("kdrive.auth.accountAction");
    return ACTIONS.includes(hint as Action) ? hint as Action : null;
  });
  const [totp, setTotp] = useState<TotpStatus | null>(null);
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [challenge, setChallenge] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [externalProof, setExternalProof] = useState(() => sessionStorage.getItem("kdrive.auth.accountExternalProof") === "true");
  useEffect(() => { sessionStorage.removeItem("kdrive.auth.accountExternalProof"); }, []);

  const { user, refreshMe } = useAuth();
  const [methods, setMethods] = useState<AuthMethodsResponse["methods"]>([]);
  const [reauthPassword, setReauthPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [passwordError, setPasswordError] = useState<{ field: "password" | "confirmation"; message: string } | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [reauthenticationExpiresAt, setReauthenticationExpiresAt] = useState<number | null>(() => {
    const stored = sessionStorage.getItem("kdrive.auth.reauthenticatedExpiresAt");
    sessionStorage.removeItem("kdrive.auth.reauthenticatedExpiresAt");
    const parsed = stored ? Date.parse(stored) : Number.NaN;
    return Number.isFinite(parsed) && parsed > Date.now() ? parsed : null;
  });
  const [deletable, setDeletable] = useState<boolean | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [membershipDepartable, setMembershipDepartable] = useState<boolean | null>(null);
  const [membershipBlockers, setMembershipBlockers] = useState<string[]>([]);
  const [membershipConfirmation, setMembershipConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const reauthenticated = reauthenticationExpiresAt !== null && reauthenticationExpiresAt > Date.now();

  const reload = async () => {
    const methodResult = await fetchAuthMethods();
    setMethods(methodResult.methods);
    setTotp(await fetchTotpStatus());
  };
  useEffect(() => { void reload().catch(() => setMessage("ログイン方法を取得できませんでした。")); }, []);
  useEffect(() => { void fetchMe().then((me) => setCurrentEmail(me.email), () => setCurrentEmail("取得できませんでした")); }, []);
  useEffect(() => {
    if (reauthenticationExpiresAt === null) return;
    const remaining = reauthenticationExpiresAt - Date.now();
    if (remaining <= 0) {
      setReauthenticationExpiresAt(null);
      return;
    }
    const timer = globalThis.setTimeout(() => setReauthenticationExpiresAt(null), remaining);
    return () => globalThis.clearTimeout(timer);
  }, [reauthenticationExpiresAt]);

  const localReauthenticate = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setMessage("");
    try {
      const result = challenge ? await verifyTotpChallenge(recovery ? { recoveryCode: code } : { code }) : await createRecentReauthentication(reauthPassword);
      setReauthPassword(""); setCode("");
      if ("totpRequired" in result) { setChallenge(true); return; }
      if (!("expiresAt" in result)) throw new Error("unexpected_authentication_result");
      setChallenge(false); setExternalProof(false);
      setReauthPassword(""); setReauthenticationExpiresAt(Date.parse(result.expiresAt)); setMessage("本人確認が完了しました。10分以内に操作してください。");
    } catch { setReauthPassword(""); setMessage("本人確認に失敗しました。"); }
    finally { setBusy(false); }
  };

  const external = async (provider: ExternalAuthProvider, intent: "link" | "reauthenticate") => {
    setBusy(true); setMessage("");
    try {
      if (intent === "reauthenticate") {
        sessionStorage.setItem("kdrive.auth.return", "/console/account/security");
        if (action) sessionStorage.setItem("kdrive.auth.accountAction", action);
        sessionStorage.removeItem("kdrive.auth.accountExternalProof");
      }
      await startExternalAuthentication(provider, { intent });
    } catch { setMessage("外部ログインを開始できませんでした。"); setBusy(false); }
  };

  const sessionEndingAction = async (action: () => Promise<void>) => {
    setBusy(true); setMessage("");
    try {
      await action();
      clearConsoleBffCsrfToken();
      notifySessionExpired();
      navigate("/login", { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "recent_reauthentication_required") {
        setReauthenticationExpiresAt(null);
        setMessage("本人確認の有効期限が切れました。もう一度本人確認してください。");
      } else if (cause instanceof ApiError && cause.status === 422 && cause.code === "password_policy_violation") {
        setNewPassword("");
        setNewPasswordConfirmation("");
        setPasswordError({
          field: "password",
          message: PASSWORD_DENYLIST_ERROR_MESSAGE,
        });
      } else {
        setMessage("操作を完了できませんでした。本人確認と現在の状態を確認してください。");
      }
      setBusy(false);
    }
  };

  const passwordAction = (action: () => Promise<void>) => {
    setMessage("");
    setPasswordError(null);
    if (!isPasswordLengthValid(newPassword)) {
      setPasswordError({ field: "password", message: PASSWORD_LENGTH_ERROR_MESSAGE });
      return;
    }
    if (newPassword !== newPasswordConfirmation) {
      setPasswordError({ field: "confirmation", message: "確認用パスワードが一致しません。" });
      return;
    }
    void sessionEndingAction(action);
  };

  const checkDeletion = async () => {
    setBusy(true); setMessage("");
    try {
      const result = await fetchAccountDeletionCheck();
      setDeletable(result.deletable);
      if (result.deletable) setAction("delete");
      setBlockers(result.blockers.map((item) => `${blockerLabel(item.kind)}: ${item.count}件`));
    } catch { setMessage("削除可否を確認できませんでした。"); }
    finally { setBusy(false); }
  };

  const beginAccountEmailChange = async () => {
    setBusy(true); setMessage("");
    try {
      await startAccountEmailChange(newEmail);
      setAction(null);
      setNewEmail("");
      setMessage("確認メールを送信しました。新しいメールアドレスに届いたリンクを、このブラウザで開いてください。");
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === "recent_reauthentication_required") {
        setReauthenticationExpiresAt(null);
        setMessage("本人確認の有効期限が切れました。もう一度本人確認してください。");
      } else {
        setMessage("アカウントメールの変更を開始できませんでした。本人確認と入力内容を確認してください。");
      }
    }
    finally { setBusy(false); }
  };

  const deleteCurrentAccount = async () => {
    setBusy(true); setMessage("");
    try {
      await deleteAccount();
      clearConsoleBffCsrfToken();
      navigate("/account-deleted", { replace: true });
      return;
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setDeletable(null);
        setBlockers([]);
        setMessage("削除条件が変わりました。削除可否をもう一度確認してください。");
        return;
      }
      try {
        await fetchMe();
        setMessage("アカウント削除は完了していません。状態を確認してから手動でやり直してください。");
      } catch (readError) {
        if (readError instanceof ApiError && readError.status === 401) {
          clearConsoleBffCsrfToken();
          notifySessionExpired();
      navigate("/login", { replace: true });
          return;
        }
        setMessage("削除結果を確認できません。自動再送はしていません。通信回復後にログイン状態を確認してください。");
      }
    } finally {
      setBusy(false);
    }
  };

  const checkMembershipDeparture = async () => {
    setBusy(true); setMessage("");
    try {
      const result = await fetchCurrentMembershipDepartureCheck();
      setMembershipDepartable(result.departable);
      setMembershipBlockers(result.blockers.map((item) => `${departureBlockerLabel(item.kind)}: ${item.count}件`));
    } catch { setMessage("組織からの退出可否を確認できませんでした。"); }
    finally { setBusy(false); }
  };

  const leaveCurrentMembership = async () => {
    setBusy(true); setMessage("");
    try {
      await departCurrentMembership();
      setMembershipConfirmation("");
      await refreshMe();
      navigate("/", { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === "recent_reauthentication_required") {
        setReauthenticationExpiresAt(null);
        setMessage("本人確認の有効期限が切れました。もう一度本人確認してください。");
      } else if (error instanceof ApiError && error.code === "membership_session_unavailable") {
        setMessage("退出は完了しましたが表示を更新できません。ページを再読み込みしてください。自動再送はしないでください。");
      } else {
        setMembershipDepartable(null);
        setMembershipBlockers([]);
        setMessage("組織から退出できませんでした。条件をもう一度確認してください。");
      }
    } finally { setBusy(false); }
  };

  const local = methods.find((item) => item.provider === "local");
  const google = methods.find((item) => item.provider === "google");
  const microsoft = methods.find((item) => item.provider === "microsoft");

  const closeAction = () => {
    setAction(null); setSetup(null); setCode(""); setReauthPassword(""); setNewPassword(""); setNewPasswordConfirmation(""); setNewEmail(""); setConfirmation(""); setChallenge(false); setRecovery(false); setPasswordError(null);
  };
  const openAction = (next: Action) => { closeAction(); setMessage(""); setAction(next); };
  const runTotp = async () => {
    setBusy(true); setMessage("");
    try {
      const proof = recovery ? { recoveryCode: code } : code ? { code } : {};
      if (action === "totp-setup" && !setup) { setSetup(await startTotpSetup(proof)); setCode(""); return; }
      const result = action === "totp-setup" ? await completeTotpSetup(setup!.token, code) : await changeTotp(action === "totp-disable" ? "disable" : "recovery-codes", proof);
      clearConsoleBffCsrfToken();
      setSetup(null); setCode("");
      if (result.recoveryCodes) { setCodes(result.recoveryCodes); setAction(null); }
      else { notifySessionExpired(); navigate("/login", { replace: true }); }
    } catch (cause) {
      setCode("");
      if (cause instanceof ApiError && cause.code === "recent_reauthentication_required") setReauthenticationExpiresAt(null);
      setMessage("操作を完了できませんでした。設定状態と本人確認を確認してください。通信失敗時は自動再送していません。");
    } finally { setBusy(false); }
  };
  return (
    <div className="console-page space-y-6">
      <PageHeader title="アカウントとログイン" description="メールアドレスとログイン方法を管理します。" />
      <div className="max-w-3xl space-y-6">
      {message && !action ? <div role="status" className="rounded-md border bg-muted p-3 text-sm">{message}</div> : null}

      {totp?.localEnabled && !totp.enabled ? <div role="alert" className="rounded-md border border-amber-500/40 p-3 text-sm">メール・パスワードログインの二要素認証が未設定です。<Button variant="link" onClick={() => openAction("totp-setup")}>設定する</Button></div> : null}
      <Card><CardHeader><CardTitle asChild><h2>ログイン方法</h2></CardTitle><CardDescription>最後の有効な方法は無効化できません。変更後はすべてのセッションが終了します。</CardDescription></CardHeader><CardContent className="space-y-4">
        {(["local", "google", "microsoft"] as const).map((provider) => {
          const method = methods.find((item) => item.provider === provider);
          return <div key={provider} className="flex items-center justify-between gap-3 rounded-md border p-3"><div><div className="font-medium">{PROVIDER_LABELS[provider]}</div><div className="text-sm text-muted-foreground">{method?.state === "enabled" ? "有効" : method?.state === "disabled" ? "無効" : "未登録"}</div></div><div className="flex gap-2">{provider === "local" ? <Button variant="outline" disabled={busy} onClick={() => openAction("password")}>{method?.state === "enabled" ? "パスワード変更" : "パスワード設定"}</Button> : null}{provider !== "local" && method?.state !== "enabled" ? <Button variant="outline" disabled={busy} onClick={() => openAction(`${provider}-link` as Action)}>{method?.state === "disabled" ? "再有効化" : "追加"}</Button> : null}{method?.state === "enabled" ? <Button variant="outline" disabled={busy} onClick={() => openAction(`${provider}-disable` as Action)}>無効化</Button> : null}</div></div>;
        })}
        {totp?.localEnabled ? <div className="rounded-md border p-3 space-y-3"><p>メール・パスワードログインの二要素認証：{totp.enabled ? "設定済み" : "未設定"}</p><div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => openAction("totp-setup")}>{totp.enabled ? "認証アプリを変更" : "二要素認証を設定"}</Button>{totp.enabled ? <><Button variant="outline" onClick={() => openAction("totp-codes")}>回復コードを再発行</Button><Button variant="outline" onClick={() => openAction("totp-disable")}>二要素認証を解除</Button></> : null}</div></div> : null}
        <p className="text-xs text-muted-foreground">Google／Microsoftの二要素認証は各サービスで管理します。</p>
      </CardContent></Card>

      <Card><CardHeader><CardTitle asChild><h2>アカウントメール</h2></CardTitle></CardHeader><CardContent className="flex items-center justify-between gap-3"><span>{currentEmail || "取得中…"}</span><Button variant="outline" disabled={busy} onClick={() => openAction("email")}>変更</Button></CardContent></Card>

      {user?.currentMembership ? <Card className="border-amber-500/40"><CardHeader><CardTitle asChild><h2>現在の組織から退出</h2></CardTitle><CardDescription>管理者・一般ユーザーのどちらも本人の意思で退出できます。最後の有効な管理者である場合や、所有設定・個人資格情報が残る場合は退出できません。アカウント自体は削除されません。</CardDescription></CardHeader><CardContent className="space-y-3"><div className="rounded-md border bg-muted p-3 text-sm"><span className="font-medium">現在の組織:</span> {user.memberships.find((item) => item.customerId === user.currentMembership?.customerId)?.customerName ?? user.currentMembership.customerId}</div><Button variant="outline" disabled={busy} onClick={() => void checkMembershipDeparture()}>退出可否を確認</Button>{membershipDepartable === false ? <ul className="list-disc pl-5 text-sm">{membershipBlockers.map((item) => <li key={item}>{item}</li>)}</ul> : null}{membershipDepartable === true ? <><Label htmlFor="membership-departure-confirmation">確認のため「この組織から退出」と入力</Label><Input id="membership-departure-confirmation" value={membershipConfirmation} onChange={(event) => setMembershipConfirmation(event.target.value)} /><Button variant="destructive" disabled={busy || membershipConfirmation !== "この組織から退出"} onClick={() => reauthenticated ? void leaveCurrentMembership() : openAction("depart")}>この組織から退出</Button></> : null}</CardContent></Card> : null}

      <div><Button variant="destructive" disabled={busy} onClick={() => { closeAction(); void checkDeletion(); }}>アカウントを削除</Button>{deletable === false ? <ul className="mt-3 list-disc pl-5 text-sm">{blockers.map(item => <li key={item}>{item}</li>)}</ul> : null}</div>
      {action ? <ActionDialog title={!reauthenticated ? "本人確認" : action === "password" ? "パスワード設定" : action === "email" ? "メールアドレス変更" : action === "delete" ? "アカウント削除" : action.startsWith("totp-") ? "二要素認証" : "ログイン設定の変更"} onClose={closeAction} busy={busy}>
        {message ? <p role="status" className="text-sm">{message}</p> : null}
        {!reauthenticated ? <div><p className="text-sm text-muted-foreground">変更操作には10分以内の本人確認が必要です。外部ログインは、EXTONE Driveに登録・追加したときと同じGoogle / Microsoftアカウントを選択してください。アカウントメールの一致だけでは本人確認になりません。</p><div className="space-y-4">
        {local?.state === "enabled" ? <form className="flex max-w-xl items-end gap-2" onSubmit={localReauthenticate}><div className="min-w-0 flex-1 space-y-2"><Label htmlFor="reauth-password">{challenge ? (recovery ? "回復コード" : "認証アプリの6桁コード") : "現在のパスワード"}</Label><Input id="reauth-password" type={challenge ? "text" : "password"} autoComplete={challenge ? "one-time-code" : "current-password"} value={challenge ? code : reauthPassword} onChange={(event) => challenge ? setCode(event.target.value) : setReauthPassword(event.target.value)} disabled={busy} />{challenge ? <Button type="button" variant="ghost" onClick={() => { setRecovery(!recovery); setCode(""); }}>{recovery ? "認証アプリを使う" : "回復コードを使う"}</Button> : null}</div><Button disabled={busy}>{challenge ? "コードを確認" : "パスワードで確認"}</Button></form> : null}
        {google?.state === "enabled" || microsoft?.state === "enabled" ? <div className="flex flex-wrap gap-2">
          {google?.state === "enabled" ? <Button variant="outline" disabled={busy} onClick={() => void external("google", "reauthenticate")}>Googleで確認</Button> : null}
          {microsoft?.state === "enabled" ? <Button variant="outline" disabled={busy} onClick={() => void external("microsoft", "reauthenticate")}>Microsoftで確認</Button> : null}
        </div> : null}
      </div></div> : <>
          {action === "password" ? <div className="grid max-w-xl gap-2">
          <Label htmlFor="new-password">{local?.state === "enabled" ? "新しいパスワード" : "新しいローカルパスワード"}</Label>
          <Input
            id="new-password"
            aria-invalid={passwordError?.field === "password"}
            aria-describedby={passwordError?.field === "password" ? "new-password-hint new-password-error" : "new-password-hint"}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => { setNewPassword(event.target.value); setPasswordError(null); }}
            disabled={busy}
          />
          <p id="new-password-hint" className="text-xs text-muted-foreground">{PASSWORD_LENGTH_HINT}</p>
          {passwordError?.field === "password" ? <p id="new-password-error" role="alert" className="text-sm text-destructive">{passwordError.message}</p> : null}
          <Label htmlFor="new-password-confirmation">新しいパスワード（確認）</Label>
          <Input
            id="new-password-confirmation"
            aria-invalid={passwordError?.field === "confirmation"}
            aria-describedby={passwordError?.field === "confirmation" ? "new-password-confirmation-error" : undefined}
            type="password"
            autoComplete="new-password"
            value={newPasswordConfirmation}
            onChange={(event) => { setNewPasswordConfirmation(event.target.value); setPasswordError(null); }}
            disabled={busy}
          />
          {passwordError?.field === "confirmation" ? <p id="new-password-confirmation-error" role="alert" className="text-sm text-destructive">{passwordError.message}</p> : null}
          {local?.state !== "enabled" ? <Button disabled={busy || !reauthenticated} onClick={() => passwordAction(() => enableLocalAuthMethod(newPassword))}>パスワードを設定</Button> : <Button disabled={busy || !reauthenticated} onClick={() => passwordAction(() => changePassword(newPassword))}>パスワードを変更</Button>}
        </div> : null}
          {action === "email" ? <div className="space-y-3"><p className="text-sm">新しいメール宛ての確認リンクで変更が完了し、ログアウトします。</p><Label htmlFor="new-account-email">新しいメールアドレス</Label><Input id="new-account-email" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} disabled={busy} /><Button disabled={busy || !newEmail} onClick={() => void beginAccountEmailChange()}>確認メールを送る</Button></div> : null}
          {action === "delete" ? <div className="space-y-3"><p>アカウントとログイン設定を削除します。この操作は取り消せません。Google／Microsoft側のアカウントは削除されません。削除受付済み組織のデータ抹消は継続します。再登録しても旧所属・権限は復元されません。</p><Label htmlFor="account-delete-confirmation">確認のためアカウント名（氏名）「{user?.fullName}」を入力</Label><Input id="account-delete-confirmation" value={confirmation} onChange={e => setConfirmation(e.target.value)} /><Button variant="destructive" disabled={busy || !user?.fullName || confirmation !== user.fullName} onClick={() => void deleteCurrentAccount()}>削除を実行</Button></div> : null}
          {action.startsWith("totp-") ? <div className="space-y-3"><p className="text-sm">メール・パスワードログインに適用します。Google／Microsoftログインには適用しません。変更後はすべてのセッションが終了します。</p>{setup ? <><img src={setup.qrDataUrl} alt="認証アプリ登録用QRコード" className="mx-auto w-48" /><p className="break-all text-sm">設定キー：{setup.secret}</p></> : null}{action === "totp-disable" ? <p>解除後のパスワードログインでは二要素認証を要求しなくなります。</p> : null}{setup || (totp?.enabled && !externalProof) ? <><Label htmlFor="totp-code">{setup ? "新しい認証アプリの6桁コード" : recovery ? "回復コード" : "現在の認証アプリの6桁コード"}</Label><Input id="totp-code" autoComplete="one-time-code" value={code} onChange={e => setCode(e.target.value)} disabled={busy} />{!setup ? <Button variant="ghost" onClick={() => { setRecovery(!recovery); setCode(""); }}>{recovery ? "認証アプリを使う" : "回復コードを使う"}</Button> : null}</> : null}<Button disabled={busy || Boolean(setup && !code)} onClick={() => void runTotp()}>{action === "totp-setup" ? setup ? "有効化する" : "設定を開始" : action === "totp-disable" ? "解除する" : "再発行する"}</Button></div> : null}
          {action.endsWith("-link") ? <Button disabled={busy} onClick={() => void external(action.startsWith("google") ? "google" : "microsoft", "link")}>ログイン方法を追加</Button> : null}
          {action.endsWith("-disable") && !action.startsWith("totp-") ? <><p>このログイン方法を無効にし、すべてのセッションを終了します。</p><Button disabled={busy} onClick={() => void sessionEndingAction(() => disableAuthMethod(action.split("-")[0] as "local" | ExternalAuthProvider))}>無効化する</Button></> : null}
          {action === "depart" ? <Button disabled={busy} onClick={() => void leaveCurrentMembership()}>この組織から退出</Button> : null}
        </>}
      </ActionDialog> : null}
      {codes ? <ActionDialog title="回復コードを保存してください" onClose={() => { notifySessionExpired(); navigate("/login", { replace: true }); }}><p>数字8桁の回復コードが10個あります。ログイン時は未使用の1行だけを入力してください。各コードは1回だけ使えます。この画面を閉じると再表示できません。安全な場所へ保存し、再度ログインしてください。</p><pre className="overflow-auto text-sm select-all">{codes.join("\n")}</pre><Button onClick={() => { setCodes(null); notifySessionExpired(); navigate("/login", { replace: true }); }}>保存したのでログインへ</Button></ActionDialog> : null}
      </div>
    </div>
  );
}

function departureBlockerLabel(kind: string) {
  if (kind === "last_active_admin") return "最後の有効な管理者";
  if (kind === "membership_current_ownership") return "移譲していない所有設定";
  return "削除していない個人資格情報";
}

function blockerLabel(kind: string) {
  if (kind === "active_membership") return "有効な組織所属";
  if (kind === "membership_current_ownership") return "解消していない所有設定";
  return "解消していない個人資格情報";
}
